import { HttpResponseStatusCodes } from '@hobt/constants';
import { ApiClient, ApiClientConfig } from 'Foundation/Api';
import { AuthenticationContextType } from 'Foundation/Authentication';
import { config } from '../../../../../../../../config';
import { PortfolioCardProps, PortfolioWorkflowActionType, WorkflowExecutionResult } from '../types';
import HbtServiceErrorCodes from '../utils/HbtServiceErrors';

interface RequestProps {
  authenticationContext: AuthenticationContextType;
  portfolioIds: number[];
}

export const HTTP_STATUS_ACCEPTED = 202;
export const HTTP_STATUS_CONFLICT = 409;

const apiClientConfig: ApiClientConfig = {
  timeout: config.portfolioLoansSummary.requestTimeout
};
const portfolioLoansTimeout: number = config.portfolioLoansSummary.requestTimeout;
const portfolioLoansApiUrl: string = config.portfolioLoansSummary.urls.portfolioLoans;
const portfolioLoansErrors: string = config.portfolioLoansSummary.urls.loansErrors;
const portfolioExecuteWorkflow: string = config.portfolioLoansSummary.urls.executeWorkflow;

const rethrowWorkflowError = (err: unknown, context: string): never => {
  if (err instanceof Error) {
    err.message = `${context}: ${err.message}`;
    throw err;
  }

  if (err && typeof err === 'object') {
    const wrappedError = new Error(context);
    (wrappedError as unknown as Record<string, unknown>).originalError = err;
    throw wrappedError;
  }

  throw new Error(`${context}: ${String(err)}`);
};

export const getPortfolioLoanDetails = async (
  authenticationContext: any,
  portfolioIds: number[]
): Promise<PortfolioCardProps[] | null> => {
  try {
    const response = await ApiClient(authenticationContext, apiClientConfig).getWithAuth(
      portfolioLoansApiUrl,
      {
        params: {
          portfolioIds: portfolioIds.join(',')
        },
        timeout: portfolioLoansTimeout
      }
    );

    return (
      ((response?.data?.statusCode === HttpResponseStatusCodes.Created ||
        response?.data?.statusCode === HttpResponseStatusCodes.OK) &&
        response?.data?.data) ||
      null
    );
  } catch (err) {
    throw new Error('Error getting portfolios:', err!!);
  }
};

export const getLoanErrors = async (authenticationContext: any): Promise<any | null> => {
  try {
    const response = await ApiClient(authenticationContext, apiClientConfig).getWithAuth(
      portfolioLoansErrors
    );

    return (
      ((response?.data?.statusCode === HttpResponseStatusCodes.Created ||
        response?.data?.statusCode === HttpResponseStatusCodes.OK) &&
        response?.data?.data) ||
      null
    );
  } catch (err) {
    throw new Error('Error getting portfolios:', err!!);
  }
};

export const executeWorkflow = async (
  authenticationContext: any,
  workflowActionType: PortfolioWorkflowActionType,
  portfolioId: string
): Promise<WorkflowExecutionResult> => {
  try {
    await ApiClient(authenticationContext, apiClientConfig).postWithAuth(
      `${portfolioExecuteWorkflow}/portfolios/${portfolioId}/workflows/${workflowActionType}`,
      {
        timeout: portfolioLoansTimeout
      }
    );

    return { success: true };
  } catch (error: any) {
    const errorCode = error.response?.data?.error?.errorCode;

    if (
      error.response?.status === HttpResponseStatusCodes.BadRequest &&
      (errorCode === HbtServiceErrorCodes.HBT_ERR_WORKFLOW_INVALID_PORTFOLIO_STATUS.code ||
        errorCode === HbtServiceErrorCodes.HBT_ERR_WORKFLOW_LOANS_IN_REVIEW.code)
    ) {
      return { success: false, errorCode };
    }

    return rethrowWorkflowError(error, 'Error getting master pool table data');
  }
};

export const releasePortfolioWorkflowStatus = async (
  authenticationContext: AuthenticationContextType,
  portfolioId: string
): Promise<boolean> => {
  try {
    await ApiClient(authenticationContext, apiClientConfig).patchWithAuth(
      `${portfolioExecuteWorkflow}/portfolios/${portfolioId}/release-status`,
      {
        timeout: portfolioLoansTimeout
      }
    );

    return true;
  } catch (error) {
    throw error;
  }
};
